import { useEffect, useState } from 'react'
import { adminApi } from '../../api/endpoints'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Input from '../ui/Input'
import type { CoinExchangeRow, CoinType } from '../../types'
import { formatDate } from '../../utils/helpers'

const COINS: { id: CoinType; label: string }[] = [
  { id: 'bronze', label: 'برنز' },
  { id: 'silver', label: 'نقره' },
  { id: 'gold', label: 'طلا' },
  { id: 'diamond', label: 'الماس' },
]

const DEFAULTS: Record<CoinType, string> = {
  bronze: '0.50',
  silver: '1.00',
  gold: '10.00',
  diamond: '100.00',
}

function centsToInput(cents: number): string {
  const whole = Math.floor(cents / 100)
  const frac = String(Math.abs(cents % 100)).padStart(2, '0')
  return `${whole}.${frac}`
}

const COIN_LABEL: Record<string, string> = {
  bronze: 'برنز',
  silver: 'نقره',
  gold: 'طلا',
  diamond: 'الماس',
}

export default function AdminCoinRatesPanel() {
  const [values, setValues] = useState<Record<CoinType, string>>(DEFAULTS)
  const [exchanges, setExchanges] = useState<CoinExchangeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await adminApi.getCoinRates()
        if (cancelled) return
        const next = { ...DEFAULTS }
        for (const coin of COINS) {
          const cents = res.rates?.[coin.id]
          if (typeof cents === 'number' && cents > 0) next[coin.id] = centsToInput(cents)
        }
        setValues(next)
        setExchanges(res.exchanges || [])
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'خطا در بارگذاری نرخ‌ها')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const save = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await adminApi.saveCoinRates(values)
      const res = await adminApi.getCoinRates()
      setExchanges(res.exchanges || [])
      setMessage('نرخ‌ها ذخیره شد')
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره نرخ')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-center py-8 text-gray-400">⏳ در حال بارگذاری...</div>

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div>
          <h3 className="font-bold text-lg">نرخ سکه (یورو)</h3>
          <p className="text-sm text-gray-400 mt-1">
            قیمت یک سکه. پیش‌فرض: برنز ۰٫۵۰، نقره ۱، طلا ۱۰، الماس ۱۰۰ یورو. کاربر با همین نرخ تبدیل می‌کند.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {COINS.map((coin) => (
            <Input
              key={coin.id}
              label={`${coin.label} (EUR)`}
              inputMode="decimal"
              value={values[coin.id]}
              onChange={(e) => setValues((prev) => ({ ...prev, [coin.id]: e.target.value }))}
            />
          ))}
        </div>
        <Button variant="primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'در حال ذخیره…' : 'ذخیره نرخ‌ها'}
        </Button>
        {message && <p className="text-sm text-green-300">{message}</p>}
        {error && <p className="text-sm text-red-300">{error}</p>}
      </Card>

      <Card className="p-4 space-y-2">
        <h4 className="font-bold">تبدیل‌های اخیر</h4>
        {exchanges.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">هنوز تبدیلی ثبت نشده</p>
        ) : (
          <div className="space-y-2">
            {exchanges.map((row) => (
              <div key={row.id} className="rounded-xl bg-gray-800/40 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    کاربر {row.user_id}: {row.spent} {COIN_LABEL[row.from_coin] || row.from_coin}
                    {' → '}
                    {row.received} {COIN_LABEL[row.to_coin] || row.to_coin}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-1" dir="ltr">
                  {formatDate(row.created_at)} · {(row.from_cents / 100).toFixed(2)}€ → {(row.to_cents / 100).toFixed(2)}€
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
